import type { AppTopic } from '@/backend/websocket'
import type { EventNamesForTopic, PayloadForEvent, WebSocketTopic } from '@/data/websocketTopics'
import { onScopeDispose, ref, watch, type Ref } from 'vue'
import { useWebSocket } from './useWebSocket'

type QueuedSubscription = {
  event: string
  handler: (payload: unknown) => void
}

/**
 * Reactive WebSocket topic subscription with automatic cleanup.
 *
 * Multiple components can subscribe to the same topic without conflicts.
 * Event listeners are automatically cleaned up when the caller goes out of scope.
 *
 * Usage:
 * ```ts
 * const { subscribe, isConnected } = useWebSocketTopic(ref('some-topic'))
 *
 * subscribe('message', (data) => {
 *   console.log('§ Received:', data)
 * })
 * ```
 */
export const useWebSocketTopic = <T extends WebSocketTopic>(topic: Ref<T | null | undefined>) => {
  const ws = useWebSocket()
  const cleanupFunctions = ref<(() => void)[]>([])
  const isSubscribed = ref(false)
  const queuedSubscriptions = ref<QueuedSubscription[]>([])

  /**
   * Apply all queued subscriptions to the current topic
   */
  const applyQueuedSubscriptions = () => {
    const currentTopic = topic.value
    if (!currentTopic) return

    queuedSubscriptions.value.forEach(({ event, handler }) => {
      const cleanup = ws.addTopicEventListener(currentTopic as AppTopic, event, handler)
      cleanupFunctions.value.push(cleanup)
    })
  }

  /**
   * Subscribe to an event on the current topic.
   * Returns a cleanup function, but cleanup is also automatic on scope dispose.
   */
  const subscribe = async <E extends EventNamesForTopic<T>>(
    event: E,
    handler: (payload: PayloadForEvent<T, E>) => void,
  ): Promise<() => void> => {
    // We need to cast the event and handler to strings to avoid very complex typing.
    // Consumer gets strict types when using this.
    const untypedEvent = event as string
    const untypedHandler = handler as (payload: unknown) => void

    if (topic.value) {
      const cleanup = ws.addTopicEventListener(
        topic.value as AppTopic,
        untypedEvent,
        untypedHandler,
      )
      cleanupFunctions.value.push(cleanup)
      return cleanup
    }

    // We may not have a topic yet, so we queue the subscription

    const queuedItem = { event: untypedEvent, handler: untypedHandler }
    queuedSubscriptions.value.push(queuedItem)

    return () => {
      queuedSubscriptions.value = queuedSubscriptions.value.filter((sub) => sub !== queuedItem)
    }
  }

  /**
   * Manually unsubscribe from the topic (usually not needed due to auto-cleanup)
   */
  const unsubscribe = async () => {
    cleanupFunctions.value.forEach((cleanup) => cleanup())
    cleanupFunctions.value = []

    // Unsubscribe from topic
    if (topic.value && isSubscribed.value) {
      await ws.unsubscribeFromTopic(topic.value as AppTopic)
      isSubscribed.value = false
    }
  }

  // Handle topic changes
  watch(
    topic,
    async (newTopic, oldTopic) => {
      if (oldTopic === newTopic) {
        return
      }

      // Clean up old topic
      if (oldTopic && isSubscribed.value) {
        await unsubscribe()
      }

      // Subscribe to new topic
      if (newTopic) {
        await ws.subscribeToTopic(newTopic as AppTopic)
        isSubscribed.value = true

        // Apply all queued subscriptions to the new topic
        applyQueuedSubscriptions()
      }
    },
    { immediate: true },
  )

  // Automatic cleanup on component unmount
  onScopeDispose(() => {
    unsubscribe()
    queuedSubscriptions.value = []
  })

  return {
    subscribe,
    isSubscribed,
  }
}
